<script setup lang="ts">
import { useRouter } from 'vue-router';
import { computed, ref, type PropType } from 'vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import * as Sentry from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { useKycSessionStore } from '@/stores/kycSession.store';
import type { Screen } from '@/models/UserFlow';
import Loading from '@/components/ui/Loading.vue';
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';

const props = defineProps({
  text: String,
  classes: String,
  preNavigationAction: Function as PropType<
    (expectedSpendAmount: string) => Promise<boolean> | boolean
  >,
  expectedSpendAmount: String,
  disabled: Boolean,
});

const isLoading = ref(false);

const router = useRouter();
const { kycSession } = storeToRefs(useKycSessionStore());

if (!kycSession.value) {
  throw new Error('kycSession is not defined');
}

const steps = kycSession.value.steps;

const nextRoute = computed(() => {
  const currentRoute = (router.currentRoute?.value.name || 'Region') as Screen;
  return steps.getNextScreen(currentRoute);
});

const dataTestValue = computed(() => `move-to-${nextRoute.value.toLowerCase()}`);

async function performNavigation() {
  isLoading.value = true;
  if (props.preNavigationAction) {
    try {
      const isActionCompletedSuccessfully = await props.preNavigationAction(
        props.expectedSpendAmount || '0'
      );
      if (!isActionCompletedSuccessfully) {
        isLoading.value = false;
        return;
      }
    } catch (error) {
      Sentry.captureException(error, {
        level: 'info',
      });
      isLoading.value = false;
      return;
    }
  }

  await router.push({ name: nextRoute.value });
  isLoading.value = false;
}
</script>

<template>
  <RoundedButton
    v-if="nextRoute !== router.currentRoute?.value.name"
    @clicked="performNavigation"
    :class="'border-light-border bg-light-white text-light-primary hover:bg-light-item dark:border-dark-disabled dark:bg-dark-list-bg dark:text-dark-primary my-2 flex h-[58px] rounded-lg border px-4! text-left'"
    :data-test="dataTestValue"
    :disabled="props.disabled || isLoading"
  >
    <Loading class="mr-2 h-5 w-5 flex-none" v-if="isLoading" />
    <span class="grow">{{ props.text }}</span>
    <ChevronRightIcon class="w-6 flex-none justify-self-end" />
  </RoundedButton>
</template>
